import { type ReactNode, useMemo } from 'react';

import { useAdvisorDashboardData } from 'hooks/useAdvisorDashboardData';
import { useGlassWidgetCompactViewport } from 'hooks/useGlassWidgetCompactViewport';
import { useNewAndNowNotifications } from 'hooks/useNewAndNowNotifications';

import { AchievementsCard } from './cards/AchievementsCard/AchievementsCard';
import { NotificationsCard } from './cards/NotificationsCard/NotificationsCard';
import { ProgramStatusCard } from './cards/ProgramStatusCard/ProgramStatusCard';
import { TodaysFocusCard } from './cards/TodaysFocusCard/TodaysFocusCard';
import { GlassCardGrid } from './GlassCardGrid/GlassCardGrid';
import { GlassCardHeader } from './GlassCardHeader/GlassCardHeader';
import { GlassWidgetCardCarousel } from './GlassWidgetCardCarousel/GlassWidgetCardCarousel';
import { getGlassWidgetVisibleCardCount, getMiddleCarouselNeedsNav } from './glassWidgetCarouselMath';

import clsx from 'clsx';
import i18n from 'i18n-react';

export interface GlassWidgetPanelProps {
  appId: number;
  rewards?: boolean;
  /** `metallic_tiers` feature flag — tier row and header star tier colors on Program Status card. */
  metallicTiers?: boolean;
  lightTheme?: boolean;
  /** Fixed sidebar: `homeLayout__sidebar--left` or `--right` (from `HomeView`). */
  position?: 'left' | 'right';
  enableFrostedBackground?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  /** Base (English) app ID for `getAdvisorDashboardData` — milestone data lives under the English app. */
  dashboardAppId?: number;
  /** Passed to `getAdvisorDashboardData` as `lang` (e.g. en-US, es-ES). */
  dashboardLangCode?: string;
}

export function GlassWidgetPanel({
  appId,
  rewards = false,
  metallicTiers = false,
  lightTheme = false,
  position = 'left',
  firstName,
  lastName,
  email,
  enableFrostedBackground = false,
  dashboardAppId,
  dashboardLangCode = 'en-US',
}: GlassWidgetPanelProps) {
  const { maxVisibleGlassWidgetCardSlots: maxVisible } = useGlassWidgetCompactViewport({
    compactViewportMaxHeightPx: 899,
    compactMaxVisibleGlassCardSlots: 3,
  });

  const {
    data: advisorData,
    error: advisorError,
    isLoading: advisorLoading,
  } = useAdvisorDashboardData(dashboardAppId ?? appId, dashboardLangCode);

  const {
    notifications: newAndNowNotifications,
    isLoading: newAndNowLoading,
    error: newAndNowError,
  } = useNewAndNowNotifications(appId);

  const profileImgUrl = useMemo(() => {
    return advisorData?.profile_picture_url?.trim() ?? null;
  }, [advisorData]);

  /** Login/session UI for Program Status: derived from `email` + `appId` props (from HomeView / storage). */
  const programStatusUserInfo = useMemo(() => {
    const emailStr = typeof email === 'string' ? email.trim() : '';
    return {
      isLoggedIn: emailStr !== '',
      profileFrameUrl:
        emailStr !== '' && appId >= 0
          ? `https://accounts.travpromobile.com/profile/?header=false&email=${encodeURIComponent(emailStr)}&app_id=${appId}`
          : '',
    };
  }, [email, appId]);

  const focusMessages = useMemo(() => advisorData?.focus?.messages ?? [], [advisorData?.focus?.messages]);

  /** Added to the todays-focus API: when `alternative_contact_us` is 1, use `contact_us_url` instead of `/main/contact`. */
  const contactUrl = useMemo(() => {
    if (advisorData?.alternative_contact_us !== 1) return null;
    const url = advisorData?.contact_us_url?.trim();
    if (!url) return null;
    return url;
  }, [advisorData?.alternative_contact_us, advisorData?.contact_us_url]);

  const focusPanelData = useMemo(
    () => ({
      isLoading: advisorLoading,
      error: advisorError,
      messages: focusMessages,
    }),
    [advisorLoading, advisorError, focusMessages],
  );

  const achievementsPanelData = useMemo(
    () => ({
      loading: advisorLoading,
      loadError: advisorError,
      rewards: advisorData?.rewards ?? null,
    }),
    [advisorLoading, advisorError, advisorData?.rewards],
  );

  const showTodaysFocusCard = advisorLoading || advisorError != null || focusMessages.length > 0;

  const showNotificationsCard = newAndNowLoading || newAndNowError != null || newAndNowNotifications.length > 0;

  const includeAchievementsCard = useMemo(() => rewards, [rewards]);

  const visibleCardCount = useMemo(
    () =>
      getGlassWidgetVisibleCardCount({
        showTodaysFocus: showTodaysFocusCard,
        showProgramStatus: true,
        showAchievements: includeAchievementsCard,
        showNotifications: showNotificationsCard,
      }),
    [showTodaysFocusCard, includeAchievementsCard, showNotificationsCard],
  );

  const pinnedTop = useMemo((): ReactNode[] => {
    if (!showTodaysFocusCard) return [];
    return [
      <TodaysFocusCard key="glass-todays-focus" variant="accent" focus={focusPanelData} lightTheme={lightTheme} />,
    ];
  }, [showTodaysFocusCard, focusPanelData, lightTheme]);

  const pinnedBottom = useMemo((): ReactNode[] => {
    if (!showNotificationsCard) return [];
    return [
      <NotificationsCard
        key={newAndNowNotifications.map((n) => String(n.id)).join('|')}
        appId={appId}
        notifications={newAndNowNotifications}
        isLoading={newAndNowLoading}
        error={newAndNowError}
        hideArrows
        lightTheme={lightTheme}
      />,
    ];
  }, [showNotificationsCard, appId, newAndNowNotifications, newAndNowLoading, newAndNowError, lightTheme]);

  /** Must match how many nodes we push into `middleCards` (ProgramStatus + optional Achievements). */
  const middleSlotCardCount = 1 + (includeAchievementsCard ? 1 : 0);

  /** Extra horizontal inset on middle-slot cards when carousel shows edge nav (`getMiddleCarouselNeedsNav`). */
  const addContentGutter = useMemo(
    () => getMiddleCarouselNeedsNav(maxVisible, pinnedTop.length + pinnedBottom.length, middleSlotCardCount),
    [maxVisible, pinnedTop.length, pinnedBottom.length, middleSlotCardCount],
  );

  const middleCards = useMemo((): ReactNode[] => {
    const firstNameStr = firstName ?? '';
    const lastNameStr = lastName ?? '';

    const middle: ReactNode[] = [
      <ProgramStatusCard
        key="glass-program-status"
        addContentGutter={addContentGutter}
        metallicTiers={metallicTiers}
        lightTheme={lightTheme}
        advisorMilestones={advisorData?.milestones ?? null}
        profilePictureUrl={profileImgUrl}
        firstName={firstNameStr}
        lastName={lastNameStr}
        isLoggedIn={programStatusUserInfo.isLoggedIn}
        profileFrameUrl={programStatusUserInfo.profileFrameUrl}
        contactUrl={contactUrl}
      />,
    ];
    if (includeAchievementsCard) {
      middle.push(
        <AchievementsCard
          key="glass-achievements"
          addContentGutter={addContentGutter}
          lightTheme={lightTheme}
          achievements={achievementsPanelData}
        />,
      );
    }
    return middle;
  }, [
    addContentGutter,
    metallicTiers,
    lightTheme,
    advisorData?.milestones,
    profileImgUrl,
    firstName,
    lastName,
    programStatusUserInfo.isLoggedIn,
    programStatusUserInfo.profileFrameUrl,
    contactUrl,
    includeAchievementsCard,
    achievementsPanelData,
  ]);

  return (
    <div
      className={clsx('homeLayout__sidebar', {
        'homeLayout__sidebar--left': position === 'left',
        'homeLayout__sidebar--right': position === 'right',
      })}
    >
      <div
        className={clsx('homeLayout__sidebarRibbon', {
          'homeLayout__sidebarRibbon--frosted': enableFrostedBackground,
          'homeLayout__sidebarRibbon--topAlignStack': visibleCardCount > 2,
          'homeLayout__sidebarRibbon--elevatedCenter': visibleCardCount <= 2,
        })}
        data-visible-glass-widget-cards={visibleCardCount}
      >
        <GlassCardGrid>
          <GlassCardHeader
            title={
              firstName
                ? (i18n.translate('glassWidgets.welcomeBack', { firstName }) as string)
                : (i18n.translate('glassWidgets.welcome') as string)
            }
          />
          <GlassWidgetCardCarousel
            maxVisible={maxVisible}
            pinnedTop={pinnedTop}
            pinnedBottom={pinnedBottom}
            middleCards={middleCards}
          />
          <GlassCardHeader
            invisible
            title={
              firstName
                ? (i18n.translate('glassWidgets.welcomeBack', { firstName }) as string)
                : (i18n.translate('glassWidgets.welcome') as string)
            }
          />
        </GlassCardGrid>
      </div>
      <div className="homeLayout__sidebarGutter" aria-hidden="true" />
    </div>
  );
}
